import { Switch } from "@headlessui/react";
import { INode } from "../../@types/device";
import { ServiceContextType } from "../../@types/service";
import { rawValue, readValue } from "../../lib/node";
import { useSetter } from "../../lib/useSetter";

export default function AcWidget({
  node,
  ctx,
}: {
  node: INode;
  ctx: ServiceContextType;
}) {
  const { setValue, onRangeChange, disabled } = useSetter();
  const ac = node.ac;
  if (!ac) return <div className="mt-4 text-sm opacity-40">AC not configured.</div>;

  const indoor = ac.indoor ? readValue(ctx, ac.indoor, node.addr) : undefined;
  const outdoor = ac.outdoor ? readValue(ctx, ac.outdoor, node.addr) : undefined;
  const target = ac.targetKey ? rawValue(ctx, node.addr, ac.targetKey) : undefined;
  const fan = ac.fanKey ? rawValue(ctx, node.addr, ac.fanKey) : undefined;
  const swing = ac.swingKey ? rawValue(ctx, node.addr, ac.swingKey) : undefined;
  const running = ac.runningKey
    ? rawValue(ctx, ac.runningAddr ?? node.addr, ac.runningKey)
    : undefined;

  return (
    <div className="pt-4">
      {ac.runningKey && (
        <div className="absolute right-4 top-3">
          <i
            className={`fa-solid fa-person-running ${
              running ? "text-green-600" : "text-gray-600"
            }`}
            title={running ? "Compressor running" : "Compressor idle"}
          />
        </div>
      )}

      <div className="flex flex-row justify-between mb-4 px-2">
        {[
          { label: "Indoor", value: indoor },
          { label: "Outdoor", value: outdoor },
        ].map((x) => (
          <div key={x.label} className="w-32 bg-slate-700/50 rounded-md p-4">
            <div className="text-center mb-1">{x.label}</div>
            <div className="flex flex-row justify-center">
              <div className="text-3xl font-medium">{x.value ?? "--"}</div>
              <div className="text-lg">°C</div>
            </div>
          </div>
        ))}
      </div>

      {ac.targetKey && (
        <div className="flex space-x-4 items-center mb-4">
          <i className="fas fa-temperature-low" />
          <div className="grow">
            <input
              type="range"
              className="w-full"
              disabled={disabled}
              value={target ?? ac.targetMin ?? 19}
              min={ac.targetMin ?? 19}
              max={ac.targetMax ?? 30}
              step={1}
              onChange={onRangeChange(node.addr, ac.targetKey)}
            />
          </div>
          <div className="w-10 text-right">{target ?? "--"}°C</div>
        </div>
      )}

      {ac.fanKey && (
        <div className="flex space-x-4 items-center mb-4">
          <i className="fas fa-fan" />
          <div className="grow">
            <input
              type="range"
              className="w-full"
              disabled={disabled}
              value={fan ?? 0}
              min={0}
              max={ac.fanMax ?? 5}
              step={1}
              onChange={onRangeChange(node.addr, ac.fanKey)}
            />
          </div>
          <div className="w-10 text-right">{fan ?? "--"}</div>
        </div>
      )}

      {ac.swingKey && (
        <div className="flex flex-row space-x-4 mb-4 items-center">
          <i className="fas fa-up-down w-4" />
          {/* Switch.onChange yields a boolean, not an event -- the old shared
              handler ran parseInt over it and wrote NaN. */}
          <Switch
            disabled={disabled}
            checked={!!swing}
            onChange={(on) => setValue(node.addr, ac.swingKey, on ? 1 : 0)}
            className="group relative flex h-7 w-14 cursor-pointer rounded-full bg-slate-500/30 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-green-500/80"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white ring-0 shadow-lg transition duration-200 ease-in-out group-data-[checked]:translate-x-7"
            />
          </Switch>
        </div>
      )}
    </div>
  );
}
